import { TOUR_IDS, type TourDefinition, type TourId } from './types';

export type TourLaunchParams = {
  tourId: TourId;
  stepIndex: number;
};

const isTourId = (value: string): value is TourId =>
  TOUR_IDS.some(tourId => tourId === value);

const toStepIndex = (step: string | undefined): number => {
  if (step === undefined) return 0;
  const stepNumber = Number(step);
  if (!Number.isInteger(stepNumber) || stepNumber < 1) return 0;
  return stepNumber - 1;
};

export const parseTourLaunchParams = ({
  tourId,
  step,
}: {
  tourId: string | undefined;
  step: string | undefined;
}): TourLaunchParams | null => {
  if (!tourId || !isTourId(tourId)) return null;
  return { tourId, stepIndex: toStepIndex(step) };
};

export const resolveLaunchStepIndex = (
  { steps }: TourDefinition,
  stepIndex: number
): number => Math.max(0, Math.min(stepIndex, steps.length - 1));

export type TourLaunchPathArgs = {
  /** '/app' for ardoq-front, '/surveys' for the surveys app. */
  basePath: string;
  tourId: TourId;
  /** 1-based, matching what `parseTourLaunchParams` reads back. */
  step: number;
  /** App-specific hand-off parameters, e.g. `{ surveyId }`. */
  params?: Record<string, string>;
};

const withoutTrailingSlash = (basePath: string): string =>
  basePath.replace(/\/+$/, '');

const toLaunchSearch = (
  step: number,
  params: Record<string, string> | undefined
): string => new URLSearchParams({ step: String(step), ...params }).toString();

export const toTourLaunchPath = ({
  basePath,
  tourId,
  step,
  params,
}: TourLaunchPathArgs): string =>
  `${withoutTrailingSlash(basePath)}/product-tour/${tourId}?${toLaunchSearch(step, params)}`;
